export const TILE_SIZE = 32;

export const COLORS = {
  magenta: 0xff2bd6,
  cyan: 0x00fff2,
  purple: 0x3a1a5c,
  purpleLight: 0x6a34a0,
  black: 0x140a24,
  white: 0xffffff,
  danger: 0xff3355,
  warnYellow: 0xffe14d,
  soldierBody: 0xff5a3c,
  droneBody: 0x33d1ff,
  turretBody: 0x8f8fb8,
  bossBody: 0xff2bd6,
  droneHeavyBody: 0x4a4e64,
  droneScoutBody: 0xc9cfe0,
  droneStealthBody: 0x1c1428,
  droneStealthAccent: 0x9a3cff,
  droneHackerAccent: 0x39ff8f,
  droneEliteAccent: 0xffb32b,
  soldierScoutBody: 0xd8dce8,
  soldierReconBody: 0x3a4a66,
  soldierAssaultBody: 0x4a1018,
  soldierShieldBody: 0x2c2c38,
  soldierStealthBody: 0x14101c,
  soldierHeavyBody: 0x33323e,
  soldierEliteBody: 0x16283a,
  stoneBase: 0x4a4560,
  stoneMid: 0x5c5678,
  stoneLight: 0x8c86ac,
  stoneDark: 0x241f38,
  stoneMortar: 0x18142a,
  playerArmor: 0x1c1e2c,
  playerArmorDark: 0x121320,
  playerRig: 0xd8dce8
};

export const TEXTURE_KEYS = {
  TILE_SOLID: 'tile-solid',
  TILE_SOLID_FILL: 'tile-solid-fill',
  TILE_PLATFORM: 'tile-platform',
  PLAYER: 'player',
  PLAYER_PROJECTILE: 'proj-player',
  ENEMY_PROJECTILE: 'proj-enemy',
  DRONE: 'enemy-drone',
  DRONE_HEAVY: 'enemy-drone-heavy',
  DRONE_SCOUT: 'enemy-drone-scout',
  DRONE_STEALTH: 'enemy-drone-stealth',
  DRONE_SHIELD: 'enemy-drone-shield',
  DRONE_HACKER: 'enemy-drone-hacker',
  DRONE_ELITE: 'enemy-drone-elite',
  DRONE_ALERT: 'enemy-drone-alert',
  TURRET: 'enemy-turret',
  SOLDIER: 'enemy-soldier',
  SOLDIER_SCOUT: 'enemy-soldier-scout',
  SOLDIER_RECON: 'enemy-soldier-recon',
  SOLDIER_ASSAULT: 'enemy-soldier-assault',
  SOLDIER_SHIELD: 'enemy-soldier-shield',
  SOLDIER_STEALTH: 'enemy-soldier-stealth',
  SOLDIER_HEAVY: 'enemy-soldier-heavy',
  SOLDIER_ELITE: 'enemy-soldier-elite',
  BOSS: 'boss-enforcer',
  HAZARD_FENCE: 'hazard-fence',
  HAZARD_FIRE: 'hazard-fire',
  PICKUP_HEALTH: 'pickup-health',
  PICKUP_CHIP: 'pickup-chip',
  PICKUP_AMMO: 'pickup-ammo',
  PARTICLE: 'particle-spark',
  BG_FAR: 'bg-far',
  BG_NEAR: 'bg-near',

  // --- Comic-art overhaul: loaded image textures (see Preload.js) ---------
  PLAYER_RUN: 'player-run',
  PLAYER_JUMP: 'player-jump',
  PLAYER_SHOOT: 'player-shoot',
  PLAYER_PORTRAIT: 'player-portrait',
  FLOOR_TILE_1: 'floor-tile-1',
  FLOOR_TILE_2: 'floor-tile-2',
  PLATFORM_ART: 'platform-art',
  PROP_1: 'prop-1',
  PROP_2: 'prop-2',
  PROP_3: 'prop-3',
  PROP_4: 'prop-4',
  HUD_GUN_ICON: 'hud-gun-icon',
  BG_FAR_1: 'bg-far-1',
  BG_FAR_2: 'bg-far-2',
  BG_FAR_3: 'bg-far-3',
  BG_FAR_4: 'bg-far-4',
  BG_FAR_5: 'bg-far-5',
  BG_FAR_6: 'bg-far-6',
  BG_NEAR_1: 'bg-near-1',
  BG_NEAR_2: 'bg-near-2',
  BG_NEAR_3: 'bg-near-3',
  BG_NEAR_4: 'bg-near-4',
  BG_NEAR_5: 'bg-near-5',
  BG_NEAR_6: 'bg-near-6',
  BG_NEAR_7: 'bg-near-7'
};

// Ordered lists for code that needs to cycle through the full sets (parallax
// compositing, prop scattering) rather than name each key individually.
export const PROP_KEYS = [TEXTURE_KEYS.PROP_1, TEXTURE_KEYS.PROP_2, TEXTURE_KEYS.PROP_3, TEXTURE_KEYS.PROP_4];

export const BG_FAR_KEYS = [
  TEXTURE_KEYS.BG_FAR_1,
  TEXTURE_KEYS.BG_FAR_2,
  TEXTURE_KEYS.BG_FAR_3,
  TEXTURE_KEYS.BG_FAR_4,
  TEXTURE_KEYS.BG_FAR_5,
  TEXTURE_KEYS.BG_FAR_6
];

// Floor_tile_1.png / Floor_tile_2.png originally had a transparent margin
// baked into the PNG around the actual drawn panel — tiling the raw image
// left that empty margin visible as a gap between the player's feet and
// the drawn floor edge. The source PNGs under public/assets/floor/ have
// since been cropped to just the content (see the sizes below, measured
// after cropping), so the floor band's tiling math in Game.js can use the
// plain texture with no custom frame/offset needed.
export const FLOOR_CONTENT_SIZE = {
  [TEXTURE_KEYS.FLOOR_TILE_1]: { w: 290, h: 127 },
  [TEXTURE_KEYS.FLOOR_TILE_2]: { w: 152, h: 156 }
};

export const BG_NEAR_KEYS = [
  TEXTURE_KEYS.BG_NEAR_1,
  TEXTURE_KEYS.BG_NEAR_2,
  TEXTURE_KEYS.BG_NEAR_3,
  TEXTURE_KEYS.BG_NEAR_4,
  TEXTURE_KEYS.BG_NEAR_5,
  TEXTURE_KEYS.BG_NEAR_6,
  TEXTURE_KEYS.BG_NEAR_7
];

export const DEPTHS = {
  BACKGROUND: 0,
  TILES: 10,
  HAZARDS: 15,
  PICKUPS: 18,
  ENEMIES: 20,
  PLAYER: 25,
  PROJECTILES: 30,
  FX: 40,
  UI: 100
};
